import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'

import { getHostFamily } from '../../../../../api/host-families/get-host-family'
import { useDataProps } from '../../../../../lib/hooks/use-data-props'
import { useNativeCountryName } from '../../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../../../lib/types/enums/common/gender.enum'
import { accomodationTypeEnumMapping } from '../../../../../lib/types/enums/staff-application/accomodation-type.enum'
import { conveyanceTypeEnumMapping } from '../../../../../lib/types/enums/staff-application/conveyance-type.enum'
import { HostFamily } from '../../../../../lib/types/models/host-family/host-family'
import { DataProps } from '../../../../../lib/types/utils'
import BirthDateWithAge from '../../../../elements/birth-date-with-age'
import DisplayImageInfo from '../../../../elements/display-image-info'
import { ItemNotFound } from '../../../../elements/item-not-found'
import LabelWithText from '../../../../elements/label-with-text'
import LoadingScreen from '../../../../elements/loading/loading-screen'
import HostFamilyStudentAppreciations from '../../../host-families/host-family-student-appreciations'
import HostFamilyParentInformation from './host-family-parent-information'

type Props = {
  view: 'complex' | 'simple'
}

type HostFamilyProps = {
  hostFamily: HostFamily
  className?: string
  view: 'complex' | 'simple'
}

function HostFamilyAccomodationDetails({ hostFamily, className, view }: HostFamilyProps) {
  const { t } = useTranslate()
  const displayDirection: 'row' | 'column' = view === 'simple' ? 'column' : 'row'
  return (
    <Box className={cn('bg-white-hover w-full rounded-lg border border-grey-light', className)}>
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Hébergement
      </Typography>
      <div className="h-4" />
      <div
        className={cn('w-full gap-4', {
          'grid grid-cols-5 mobile:grid-cols-1 tab:grid-cols-2 smallLaptop:grid-cols-4':
            view === 'simple',
          'flex flex-col justify-between': view === 'complex'
        })}
      >
        {view === 'complex' && (
          <>
            <LabelWithText
              display={displayDirection}
              label="Type d'hébergement"
              data={t(accomodationTypeEnumMapping[hostFamily.accomodationType])}
            />
            <LabelWithText
              display={displayDirection}
              label="Jardin"
              data={hostFamily.withGarden ? 'Oui' : 'Non'}
            />
            <LabelWithText
              display={displayDirection}
              label="Nombre de chambres"
              data={hostFamily.numberOfBedrooms}
            />
          </>
        )}
        <LabelWithText
          display={displayDirection}
          label="Animaux de compagnie"
          data={hostFamily.hasPets ? 'Oui' : 'Non'}
        />
        <LabelWithText
          display={displayDirection}
          label={
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              Quel type d'animal de
              <br />
              compagnie ?
            </Typography>
          }
          data={hostFamily.petDescription ?? '-'}
        />
        <LabelWithText
          display={displayDirection}
          label={
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              Comment se rendre
              <br />
              jusqu'au lieu des cours
            </Typography>
          }
          data={t(conveyanceTypeEnumMapping[hostFamily.transportToSchool]) ?? '-'}
        />
        <LabelWithText
          display={displayDirection}
          label={
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              Temps de trajet jusqu'à l'école
            </Typography>
          }
          data={
            <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
              {`${hostFamily.timeToSchool} mins`}
            </Typography>
          }
        />
        <LabelWithText
          display={displayDirection}
          label="Pick-up point"
          data={hostFamily.pickupPoint ?? '-'}
        />
        {view === 'complex' && (
          <>
            <LabelWithText
              display={displayDirection}
              label="Loisirs"
              data={
                <div className="flex flex-wrap gap-1">
                  {!hostFamily.hobbies ? (
                    <Badge variant="grey" text="Pas de loisirs" />
                  ) : (
                    <Badge variant="primary" text={hostFamily.hobbies} />
                  )}
                </div>
              }
            />
            <LabelWithText
              display={displayDirection}
              label="Détails de la visite"
              data={
                <div className="flex items-center gap-3">
                  <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                    {valueRender(hostFamily.visitedBy)}
                  </Typography>
                  <Badge
                    color={tailwindColors.primary.DEFAULT}
                    textColor={tailwindColors.white.DEFAULT}
                    text={valueRender(hostFamily.dateOfVisit, (date) =>
                      DateFormatter.DDMMYYYY(date)
                    )}
                  />
                </div>
              }
            />
          </>
        )}
      </div>
    </Box>
  )
}

function HostFamilyAddressDetails({ hostFamily, className }: HostFamilyProps) {
  const nativeCountryName = useNativeCountryName()
  return (
    <Box className={cn('bg-white-hover w-full rounded-lg border border-grey-light', className)}>
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Adresse
      </Typography>
      <div className="h-4" />
      <div className="flex flex-col gap-4">
        <LabelWithText
          display="row"
          label="Adresse"
          data={
            <AddressLink
              address1={hostFamily.address1}
              city={hostFamily.city}
              zipCode={hostFamily.zipCode}
              country={nativeCountryName(hostFamily.country)}
            />
          }
        />
        <LabelWithText
          display="row"
          label="Complément d'adresse"
          data={hostFamily.address2 ?? '-'}
        />
        <LabelWithText display="row" label="Ville" data={hostFamily.city} />
        <LabelWithText display="row" label="Code postal" data={hostFamily.zipCode} />
        <LabelWithText display="row" label="Pays" data={nativeCountryName(hostFamily.country)} />
      </div>
    </Box>
  )
}

const HostFamilyInformationData: React.FC<DataProps<HostFamily, Props>> = (props) => {
  const { data: hostFamily, isPending, view } = useDataProps(props, (id) => getHostFamily(id))

  if (isPending) return <LoadingScreen />
  if (!hostFamily) return <ItemNotFound title="Famille d'accueil non trouvée" />

  if (view === 'simple') {
    return (
      <div className="grid grid-cols-3 gap-4 tab:grid-cols-1 mobile:flex mobile:flex-col tab:flex tab:flex-col">
        <HostFamilyParentInformation
          view={'simple'}
          hostFamily={hostFamily}
          className="col-span-2 row-span-1 tab:col-span-1"
        />
        <HostFamilyAddressDetails
          view={'simple'}
          hostFamily={hostFamily}
          className="col-span-1 row-span-1 tab:col-span-1"
        />
        <HostFamilyAccomodationDetails
          view={'simple'}
          hostFamily={hostFamily}
          className="col-span-3 row-span-2 tab:col-span-3"
        />
      </div>
    )
  }
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 w-full flex flex-col gap-4">
          <HostFamilyParentInformation view={'complex'} hostFamily={hostFamily} />

          <Box className="bg-white-hover rounded-lg border border-grey-light">
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Enfants
            </Typography>
            <div className="h-3" />
            {hostFamily?.children.length === 0 ? (
              <Typography variant="label" color={tailwindColors.secondary.DEFAULT}>
                Pas d'enfants
              </Typography>
            ) : (
              hostFamily?.children.map((child, index) => (
                <div key={index} className="mb-4 flex">
                  <div className="rounded-full bg-secondary-light h-6 w-6 p-2 flex items-center justify-center">
                    <Typography variant="label-semibold" color={tailwindColors.secondary.DEFAULT}>
                      {index + 1}
                    </Typography>
                  </div>
                  <div className="w-5" />
                  <div className="grid grid-cols-5 gap-5">
                    <LabelWithText label="NOM" data={child.lastName.toUpperCase()} />
                    <LabelWithText
                      label="Prénom"
                      data={StringFormatter.capitalize(child.firstName)}
                    />
                    <BirthDateWithAge birthDate={child.birthDate} />
                    <div />
                    <LabelWithText
                      label="Sexe"
                      data={genderOneLetterMapping[child.gender].label}
                    />
                  </div>
                </div>
              ))
            )}
          </Box>
          <Box className="bg-white-hover rounded-lg border border-grey-light">
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Autre(s) personne(s) vivant à la maison
            </Typography>
            <div className="h-3" />
            {hostFamily?.relatives.length == 0 ? (
              <Typography variant="label" color={tailwindColors.secondary.DEFAULT}>
                Pas de personnes vivant dans la maison
              </Typography>
            ) : (
              hostFamily?.relatives.map((data, index) => (
                <div key={index} className="mb-4 flex">
                  <div className="rounded-full bg-secondary-light h-6 w-6 p-2 flex items-center justify-center">
                    <Typography variant="label-semibold" color={tailwindColors.secondary.DEFAULT}>
                      {index + 1}
                    </Typography>
                  </div>
                  <div className="w-5" />
                  <div className="grid grid-cols-6 gap-3 ">
                    <LabelWithText label="NOM" data={data.lastName.toUpperCase()} />
                    <LabelWithText
                      label="Prénom"
                      data={StringFormatter.capitalize(data.firstName)}
                    />
                    <BirthDateWithAge birthDate={data.birthDate} />
                    <div />
                    <LabelWithText label="Sexe" data={genderOneLetterMapping[data.gender].label} />
                    <LabelWithText label="Lien avec la famille" data={data.relationToFamily} />
                  </div>
                </div>
              ))
            )}
          </Box>
        </div>

        <div className="w-full">
          <HostFamilyAddressDetails view={'complex'} hostFamily={hostFamily} />

          <div className="mb-4" />

          <HostFamilyAccomodationDetails view={'complex'} hostFamily={hostFamily} />

          <div className="h-4" />
          <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Other Information
            </Typography>
            <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
              {valueRender(hostFamily.otherInformation)}
            </Typography>
          </Box>
        </div>
      </div>
      <div className="h-4" />

      <Box className="bg-primary-light rounded-lg">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          Détail hébergement des jeunes
        </Typography>
        <div className="h-4" />
        <div className="w-full grid grid-cols-2 gap-4">
          <LabelWithText
            label="Type de chambre"
            data={hostFamily.isSharedRoom ? 'Chambre partagée' : 'Chambre individuelle'}
          />
          <LabelWithText
            label="Commentaire"
            className="break-all"
            data={hostFamily.studentAccomodationComment ?? '-'}
          />
        </div>
      </Box>
      <Box className="bg-white-hover rounded-lg">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          Photos / vidéos
        </Typography>
        <div className="h-4" />
        {hostFamily.picturesAndVideos.length > 0 ? (
          hostFamily.picturesAndVideos.map((file, index) => (
            <DisplayImageInfo
              key={index}
              file={file}
              imageSizeVariant={'large'}
              informationSection={<Badge variant="white" text={valueRender(file.caption)} />}
            />
          ))
        ) : (
          <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
            Aucune photo / vidéo fournie
          </Typography>
        )}
      </Box>
      <div className="h-4" />
      <HostFamilyStudentAppreciations id={hostFamily.id} />
    </>
  )
}

export default HostFamilyInformationData
